import { Command, Plugin } from '@ckeditor/ckeditor5-core';
import { Collection } from '@ckeditor/ckeditor5-utils';
import { Widget, toWidget, viewToModelPositionOutsideModelElement } from '@ckeditor/ckeditor5-widget';
import {
  ListDropdownButtonDefinition,
  ListDropdownGroupDefinition,
  ListDropdownSeparatorDefinition,
  Model,
  addListToDropdown,
  createDropdown,
} from '@ckeditor/ckeditor5-ui';
import { SchemaContextDefinition } from '@ckeditor/ckeditor5-engine/src/model/schema';
import { Variable, VariableType } from 'src/app/shared/types/common';

type ListDropdownButtonDefinitionType = 'button' | 'switchbutton';

const VARIABLE_BLOCK_CLASS_NAME = 'ck-variable-placeholder';

function getDropdownItemsDefinitions(groupName: string, variables: Variable[]) {
  const itemsDefinitions = new Collection<ListDropdownButtonDefinition>();

  variables.forEach((variable) => {
    const definition = {
      type: 'button' as ListDropdownButtonDefinitionType,
      model: new Model({
        commandParam: variable.placeholder,
        label: variable.placeholder,
        withText: true,
      }),
    };

    itemsDefinitions.add(definition);
  });

  const groupDefinition = {
    type: 'group',
    label: groupName,
    items: itemsDefinitions,
  } as ListDropdownGroupDefinition;

  const separatorDefinition = {
    type: 'separator',
  } as ListDropdownSeparatorDefinition;

  const groupCollection = new Collection<ListDropdownGroupDefinition | ListDropdownSeparatorDefinition>();
  groupCollection.add(groupDefinition);
  groupCollection.add(separatorDefinition);

  return groupCollection;
}

class VariablesInsertVariableCommand extends Command {
  execute({ value }) {
    const editor = this.editor;
    const selection = editor.model.document.selection;

    editor.model.change((writer) => {
      // Create a <variables> element with the "name" attribute (and all the selection attributes)...
      const variables = writer.createElement('variables', {
        ...Object.fromEntries(selection.getAttributes()),
        name: value,
      });

      // ... and insert it into the document.
      editor.model.insertContent(variables);
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;

    const isAllowed = model.schema.checkChild(selection.focus.parent as SchemaContextDefinition, 'variables');

    this.isEnabled = isAllowed;
  }
}

class VariablesUI extends Plugin {
  init() {
    const editor = this.editor;
    const t = editor.t;

    // The "variables" dropdown must be registered among the UI components of the editor
    // to be displayed in the toolbar.

    const makeVariablesDropdown = (variables: Variable[]) => {
      editor.ui.componentFactory.add('variables', (locale) => {
        const dropdownView = createDropdown(locale);

        dropdownView.class = 'ck-variables-dropdown';
        dropdownView.buttonView.set({
          // The t() function helps localize the editor. All strings enclosed in t() can be
          // translated and change when the language of the editor changes.
          label: t('Variáveis'),
          tooltip: true,
          withText: true,
        });

        // Disable the variables button when the command is disabled.
        const command = editor.commands.get('variables:insertVariable');
        dropdownView.bind('isEnabled').to(command);

        // Execute the command when the dropdown item is clicked (executed).
        this.listenTo(dropdownView, 'execute', (event) => {
          editor.execute('variables:insertVariable', { value: (event.source as any).commandParam });
          editor.editing.view.focus();
        });

        const fillDropdown = (dropdownView, variables: Variable[]) => {
          dropdownView.panelView.children.clear();

          const types = new Set(variables.map((variable) => variable.type));

          types.forEach((type) => {
            if (type === VariableType.ENTITY) {
              const entities = new Set(variables.map((variable) => variable.entity));
              entities.forEach((entity) => {
                const entityVariables = variables
                  .filter((variable) => variable.type === VariableType.ENTITY)
                  .filter((variable) => variable.entity === entity);
                if (entityVariables.length) {
                  addListToDropdown(dropdownView, getDropdownItemsDefinitions(entity, entityVariables));
                }
              });
            }

            if (type === VariableType.GLOBAL) {
              const globalVariables = variables.filter((variable) => variable.type === type);
              if (globalVariables.length) {
                addListToDropdown(dropdownView, getDropdownItemsDefinitions('Geral', globalVariables));
              }
            }
          });
        };

        fillDropdown(dropdownView, variables);

        this.editor.on('variables:set-variables', (_, updatedVariables) => {
          dropdownView.isEnabled = Boolean(updatedVariables.length);
          fillDropdown(dropdownView, updatedVariables);
        });

        return dropdownView;
      });
    };

    makeVariablesDropdown(this.editor.config.get('variables') as Variable[]);
  }
}

class VariablesEditing extends Plugin {
  static get requires() {
    return [Widget];
  }

  init() {
    this._defineSchema();
    this._defineConverters();

    this.editor.commands.add('variables:insertVariable', new VariablesInsertVariableCommand(this.editor));

    this.editor.editing.mapper.on(
      'viewToModelPosition',
      viewToModelPositionOutsideModelElement(this.editor.model, (viewElement) => viewElement.hasClass(VARIABLE_BLOCK_CLASS_NAME))
    );
  }

  _defineSchema() {
    const schema = this.editor.model.schema;

    schema.register('variables', {
      // Allow wherever text is allowed:
      allowWhere: '$text',

      // The variables will act as an inline node:
      isInline: true,

      // The inline widget is self-contained so it cannot be split by the caret and it can be selected:
      isObject: true,

      // The inline widget can have the same attributes as text (for example linkHref, bold).
      allowAttributesOf: '$text',

      // The variables can have many types, like date, name, surname, etc:
      allowAttributes: ['name', 'key'],
    });
  }

  _defineConverters() {
    const conversion = this.editor.conversion;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'span',
        classes: [VARIABLE_BLOCK_CLASS_NAME],
      },
      model: (viewElement, { writer: modelWriter }) => {
        // Extract the "name" from "{name}".
        const name = (viewElement.getChild(0) as any).data;

        return modelWriter.createElement('variables', { name });
      },
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'variables',
      view: (modelItem, { writer: viewWriter }) => {
        const widgetElement = createVariablesView(modelItem, viewWriter);

        // Enable widget handling on a variables element inside the editing view.
        return toWidget(widgetElement, viewWriter);
      },
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'variables',
      view: (modelItem, { writer: viewWriter }) => createVariablesView(modelItem, viewWriter),
    });

    // Helper method for both downcast converters.
    function createVariablesView(modelItem, viewWriter) {
      const name = modelItem.getAttribute('name');

      const variablesView = viewWriter.createContainerElement('span', {
        class: VARIABLE_BLOCK_CLASS_NAME,
      });

      // Insert the variables name (as a text).
      const innerText = viewWriter.createText(name);
      viewWriter.insert(viewWriter.createPositionAt(variablesView, 0), innerText);

      return variablesView;
    }
  }
}

class Variables extends Plugin {
  static get requires() {
    return [VariablesEditing, VariablesUI];
  }
}

export default Variables;
